import React, {useEffect, useMemo, useState} from 'react';
import type {IAssignmentLogger} from '@eppo/js-client-sdk';
import * as Eppo from '@eppo/js-client-sdk'; // eslint-disable-line import/no-namespace
import {captureException} from '@sentry/browser';
import {Provider as EppoProvider} from '@ezcater/eppo-react';

import LoadingSpinner from '../../components/LoadingSpinner';
import appConfig from '../../appConfig';

const DEFAULT_TRACKING_ID = 'anonymous';

const assignmentLogger: IAssignmentLogger = {
  logAssignment: assignment => {
    console.log('eppo assignment', assignment);
  },
};

type AssignmentsProviderProps = {
  children: React.ReactNode;
  overridesConfiguration?: Record<string, boolean | string | number | Record<string, unknown>>;
  trackingId?: string;
  /**
   * Initialize Eppo in offline mode. This is meant for automated tests.
   */
  useOfflineMode?: boolean;
};

const AssignmentsProvider = ({
  children,
  overridesConfiguration = {},
  trackingId = DEFAULT_TRACKING_ID,
  useOfflineMode = false,
}: AssignmentsProviderProps) => {
  const [isInitialized, setIsInitialized] = useState(useOfflineMode);

  useEffect(() => {
    if (isInitialized) {
      return;
    }

    if (useOfflineMode || !appConfig.eppoApiKey) {
      Eppo.offlineInit({flagsConfiguration: {}});
      setIsInitialized(true);
    } else {
      Eppo.init({apiKey: appConfig.eppoApiKey, assignmentLogger, throwOnFailedInitialization: true})
        .catch(e => {
          console.error(e);
          captureException(e);
        })
        .finally(() => setIsInitialized(true));
    }
  }, [isInitialized, useOfflineMode]);

  const subjectAttributes = useMemo(() => ({}), []);

  if (!isInitialized) {
    return <LoadingSpinner />;
  }

  return (
    <EppoProvider
      overrides={overridesConfiguration}
      subjectAttributes={subjectAttributes}
      subjectKey={trackingId}
    >
      {children}
    </EppoProvider>
  );
};

export default AssignmentsProvider;
